import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, type FunctionComponent } from 'react';
import { useSupportProviderSyncOnExit } from '@/features/client-onboarding/hooks/use-support-provider-sync-on-exit';
import {
  useOnboardingNavigationStore,
  useOnboardingNavigationActions,
} from '@/features/client-onboarding/stores/onboarding-navigation-store';

import {
  ONBOARDING_SLIDES,
  SLIDE_TITLES,
  getFilteredSlides,
  isFilteredFromFullList,
} from '@/features/client-onboarding/lib/slide-config';
import { selectClient, selectFeatureFlags, selectClientSupportProviderSet, selectSession } from '@/model';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

interface UseOnboardingNavigationOptions {
  slidePage: FunctionComponent<ClientIdProps>;
}

export function useOnboardingNavigation({ slidePage }: UseOnboardingNavigationOptions) {
  const client = useSelector(selectClient)!;
  const featureFlags = useSelector(selectFeatureFlags);
  const { sessionType, clientStripe } = useSelector(selectSession);
  const { flags: appConfigFlags } = useFeatureFlags();
  const clientSupportProviderSet = useSelector(selectClientSupportProviderSet);
  const [searchParams] = useSearchParams();
  const fromLtcPlanning = searchParams.get('fromLtcPlanning') === 'true';

  const { updateSlide, reset } = useOnboardingNavigationActions();
  const slideHistory = useOnboardingNavigationStore(state => state.slideHistory);
  const previousSlideIndex = useOnboardingNavigationStore(state => state.previousSlideIndex);

  const { updateClientSupportProviderSet } = useSupportProviderSyncOnExit(clientSupportProviderSet);

  const filteredSlides = useMemo(
    () => getFilteredSlides(client, featureFlags, client.careEnvironmentSelections, appConfigFlags, sessionType, clientStripe),
    [client, featureFlags, appConfigFlags, sessionType, clientStripe],
  );

  const globalSlideIndex = ONBOARDING_SLIDES.indexOf(slidePage);
  const relativeSlideIndex = filteredSlides.indexOf(slidePage);
  const isFiltered = isFilteredFromFullList(filteredSlides);
  const slideTitle = SLIDE_TITLES.get(slidePage) ?? '';
  const isFirstSlide = relativeSlideIndex === 0;
  const isLastSlide = relativeSlideIndex === filteredSlides.length - 1;

  useEffect(() => {
    updateSlide(globalSlideIndex);
  }, [globalSlideIndex, updateSlide]);

  const getNextSlide = useCallback(
    (explicitNext?: FunctionComponent<ClientIdProps>) => {
      if (explicitNext && filteredSlides.includes(explicitNext)) {
        return explicitNext;
      }
      const nextIndex = relativeSlideIndex + 1;
      return filteredSlides[nextIndex] ?? filteredSlides[relativeSlideIndex];
    },
    [filteredSlides, relativeSlideIndex],
  );

  const getPrevSlide = useCallback(
    (explicitPrev?: FunctionComponent<ClientIdProps>) => {
      if (explicitPrev && filteredSlides.includes(explicitPrev)) {
        return explicitPrev;
      }
      const prevIndex = relativeSlideIndex - 1;
      return filteredSlides[prevIndex] ?? filteredSlides[relativeSlideIndex];
    },
    [filteredSlides, relativeSlideIndex],
  );

  const syncSupportProviders = useCallback(() => {
    updateClientSupportProviderSet(clientSupportProviderSet);
  }, [updateClientSupportProviderSet, clientSupportProviderSet]);

  return {
    filteredSlides,
    globalSlideIndex,
    relativeSlideIndex,
    isFiltered,
    slideTitle,
    isFirstSlide,
    isLastSlide,
    slideHistory,
    previousSlideIndex,
    fromLtcPlanning,
    getNextSlide,
    getPrevSlide,
    syncSupportProviders,
    reset,
    client,
  };
}
